import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

/**
 * Lightweight markdown → HTML for AI agent chat bubbles (bold, italic, code, lists, tables, breaks).
 * Escapes HTML first; no external markdown dependency.
 */
@Pipe({ name: "aiAgentMarkdown" })
export class AiAgentMarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (value == null || value === "") {
      return this.sanitizer.bypassSecurityTrustHtml("");
    }
    return this.sanitizer.bypassSecurityTrustHtml(renderAiAgentMarkdown(value));
  }
}

export function renderAiAgentMarkdown(src: string): string {
  let text = escapeHtml(src);

  // Model often emits literal <br> / <br/>; after escape they would show as text
  text = text.replace(/&lt;br\s*\/?&gt;/gi, "<br>");

  // fenced code blocks
  text = text.replace(/```([\s\S]*?)```/g, (_m, code) => {
    return `<pre class="ai-md-pre"><code>${String(code).replace(/^\n+|\n+$/g, "")}</code></pre>`;
  });

  // GFM tables (before lists/paragraphs)
  text = renderMarkdownTables(text);

  // inline code
  text = text.replace(/`([^`\n]+)`/g, '<code class="ai-md-code">$1</code>');

  // bold **...** or __...__
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");

  // italic *...* or _..._
  text = text.replace(/(^|[^\*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  text = text.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, "$1<em>$2</em>");

  // headings
  text = text.replace(/^### (.+)$/gm, '<h4 class="ai-md-h">$1</h4>');
  text = text.replace(/^## (.+)$/gm, '<h3 class="ai-md-h">$1</h3>');
  text = text.replace(/^# (.+)$/gm, '<h2 class="ai-md-h">$1</h2>');

  // unordered lists
  text = text.replace(/(?:^|\n)((?:[-*] .+(?:\n|$))+)/g, (block) => {
    const items = block
      .trim()
      .split(/\n/)
      .map((line) => line.replace(/^[-*] /, "").trim())
      .filter(Boolean)
      .map((item) => `<li>${item}</li>`)
      .join("");
    return `\n<ul class="ai-md-ul">${items}</ul>\n`;
  });

  // ordered lists
  text = text.replace(/(?:^|\n)((?:\d+\. .+(?:\n|$))+)/g, (block) => {
    const items = block
      .trim()
      .split(/\n/)
      .map((line) => line.replace(/^\d+\. /, "").trim())
      .filter(Boolean)
      .map((item) => `<li>${item}</li>`)
      .join("");
    return `\n<ol class="ai-md-ol">${items}</ol>\n`;
  });

  // paragraphs / line breaks (skip if already block tags)
  const parts = text.split(/\n{2,}/);
  text = parts
    .map((p) => {
      const trimmed = p.trim();
      if (!trimmed) {
        return "";
      }
      if (/^<(ul|ol|pre|h[2-4]|div)\b/i.test(trimmed)) {
        return trimmed;
      }
      return `<p class="ai-md-p">${trimmed.replace(/\n/g, "<br>")}</p>`;
    })
    .join("");

  return text;
}

function renderMarkdownTables(text: string): string {
  const lines = text.split("\n");
  const out: string[] = [];
  let i = 0;

  const isSep = (line: string) =>
    /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line.trim());
  const isRow = (line: string) => {
    const t = line.trim();
    return t.includes("|") && !/^<[a-z]/i.test(t);
  };
  const splitRow = (line: string) =>
    line
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  while (i < lines.length) {
    if (!isRow(lines[i])) {
      out.push(lines[i]);
      i++;
      continue;
    }

    const block: string[] = [];
    while (i < lines.length && isRow(lines[i])) {
      block.push(lines[i]);
      i++;
    }

    if (block.length < 2) {
      out.push(...block);
      continue;
    }

    let headerLine = block[0];
    let bodyStart = 1;
    if (isSep(block[1])) {
      bodyStart = 2;
    } else if (isSep(block[0])) {
      out.push(...block);
      continue;
    }

    const headers = splitRow(headerLine);
    if (!headers.length) {
      out.push(...block);
      continue;
    }

    const bodyLines = block.slice(bodyStart).filter((l) => !isSep(l));
    const thead =
      "<thead><tr>" +
      headers.map((h) => `<th>${h}</th>`).join("") +
      "</tr></thead>";
    const tbody =
      "<tbody>" +
      bodyLines
        .map((line) => {
          const cells = splitRow(line);
          while (cells.length < headers.length) {
            cells.push("");
          }
          return (
            "<tr>" +
            headers
              .map((_, idx) => `<td>${cells[idx] != null ? cells[idx] : ""}</td>`)
              .join("") +
            "</tr>"
          );
        })
        .join("") +
      "</tbody>";

    out.push(
      `<div class="ai-md-table-wrap">` +
        `<div class="ai-md-table-toolbar">` +
        `<button type="button" class="ai-md-table-export" title="Excel">Excel</button>` +
        `</div>` +
        `<table class="ai-md-table">${thead}${tbody}</table>` +
        `</div>`
    );
  }

  return out.join("\n");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
